import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import moment from 'moment';
import { appointmentStatus } from '../../../config/app.config';
import { useGetAppointmentStatusHistory } from '../../../hooks/appointments/useGetAppointmentHistory';
import {
  formatGalleryPhotoSentAt,
  getCloudinaryGalleryTileUrl,
} from '../../Web/PetPhotoGallery/petPhotoGalleryUtils';
import { ImageGalleryModal } from '../ContentModals';
import { useNestedModal } from '../AppointmentStatusesArchive/AppointmentStatusesArchive.hooks';

function getGalleryPhotoUrl(entry) {
  if (typeof entry === 'string' && entry) {
    return entry;
  }
  if (entry && typeof entry.url === 'string' && entry.url) {
    return entry.url;
  }
  return null;
}

function buildStayPhotosFromMessages(messages = []) {
  const photos = [];
  messages.forEach((message) => {
    const gallery = Array.isArray(message?.gallery) ? message.gallery : [];
    gallery.forEach((entry) => {
      const url = getGalleryPhotoUrl(entry);
      if (!url) {
        return;
      }
      photos.push({
        url,
        dateTime: message.dateTime,
      });
    });
  });
  return photos;
}

function parseAppointmentDate(dateValue) {
  if (!dateValue) {
    return null;
  }
  const parsed = moment(dateValue, 'MM/DD/YYYY', true);
  if (parsed.isValid()) {
    return parsed;
  }
  const fallback = moment(dateValue);
  return fallback.isValid() ? fallback : null;
}

function formatStayPhotosDate(dateValue) {
  const parsed = parseAppointmentDate(dateValue);
  return parsed ? parsed.format('MMM D, YYYY') : String(dateValue || '');
}

function formatBoardingStayRange(start, end) {
  if (start.year() === end.year()) {
    return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
  }
  return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
}

function getNormalizedTypeTitle(appointment) {
  return String(appointment?.typeTitle || appointment?.type || '')
    .trim()
    .toLowerCase();
}

function isBoardingStayType(typeTitle) {
  return typeTitle === 'boarding stay' || typeTitle === 'boarding';
}

function buildStayPhotosHeaderLine3(appointment, isBoardingOnly) {
  if (!isBoardingOnly || !appointment) {
    return '';
  }

  const typeTitle = getNormalizedTypeTitle(appointment);
  const dateLabel = formatStayPhotosDate(appointment.dateTime);

  if (isBoardingStayType(typeTitle)) {
    const start = parseAppointmentDate(appointment.dateTime);
    if (!start) {
      return '';
    }

    const status = appointment.status;
    const isOngoing =
      status === appointmentStatus.upcoming ||
      status === appointmentStatus.ongoing;

    if (isOngoing) {
      return `Boarding · ${start.format('MMM D, YYYY')} – Present`;
    }

    const durationDays = Number(appointment.boardingDurationDays);
    if (Number.isFinite(durationDays) && durationDays >= 1) {
      const end = start.clone().add(durationDays - 1, 'days');
      return `Boarding · ${formatBoardingStayRange(start, end)}`;
    }

    return `Boarding · ${start.format('MMM D, YYYY')}`;
  }

  if (typeTitle === 'daycare') {
    return dateLabel ? `Daycare · ${dateLabel}` : 'Daycare';
  }

  if (typeTitle === 'other') {
    return dateLabel ? `Other · ${dateLabel}` : 'Other';
  }

  const rawType = String(appointment.typeTitle || appointment.type || '').trim();
  return rawType && dateLabel ? `${rawType} · ${dateLabel}` : rawType;
}

export function StayPhotosModal({
  isOpen,
  onClose,
  appointment = null,
  isBoardingOnly = false,
}) {
  const facilityUtcOffset =
    useSelector((state) => state.user?.profile?.facility?.utcOffset) || 0;
  const imagesModal = useNestedModal();

  const appointmentId = appointment?.id || '';

  const { data, isLoading } = useGetAppointmentStatusHistory({
    appointmentId,
    enabled: !!isOpen && !!appointmentId,
  });

  const photos = useMemo(
    () => buildStayPhotosFromMessages(data?.messages || []),
    [data?.messages],
  );

  const headerLine3 = useMemo(
    () => buildStayPhotosHeaderLine3(appointment, isBoardingOnly),
    [appointment, isBoardingOnly],
  );

  const petName = appointment?.petName || '—';
  const parentName = appointment?.parentName || '—';

  const handlePhotoClick = (index) => {
    imagesModal.open({
      urls: photos.map((photo) => photo.url),
      initialIndex: index,
    });
  };

  const handleStayPhotosClose = () => {
    if (imagesModal.isOpen) {
      imagesModal.close();
    }
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={!!isOpen && !!appointment}
        size="xl"
        keyboard
        toggle={handleStayPhotosClose}
        className="model-form stay-photos-modal"
      >
        <ModalHeader>
          <div className="stay-photos-modal__title-block">
            <div className="stay-photos-modal__line">Photo Wall</div>
            <div className="stay-photos-modal__line">
              {petName} · {parentName}
            </div>
            {headerLine3 ? (
              <div className="stay-photos-modal__line stay-photos-modal__line--meta">
                {headerLine3}
              </div>
            ) : null}
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="pet-photo-gallery stay-photos-modal__gallery">
            {isLoading ? (
              <p className="stay-photos-modal__status">Loading photos…</p>
            ) : photos.length === 0 ? (
              <p className="stay-photos-modal__status">No photos to display</p>
            ) : (
              <div className="pet-photo-gallery__grid stay-photos-modal__grid">
                {photos.map((photo, index) => (
                  <button
                    key={`${photo.url}-${photo.dateTime || ''}-${index}`}
                    type="button"
                    className="pet-photo-gallery__photo"
                    onClick={() => handlePhotoClick(index)}
                  >
                    <img
                      src={getCloudinaryGalleryTileUrl(photo.url)}
                      alt=""
                      loading="lazy"
                    />
                    <span
                      className="pet-photo-gallery__photo-overlay"
                      aria-hidden="true"
                    >
                      <span className="pet-photo-gallery__photo-overlay-sent">
                        {formatGalleryPhotoSentAt(
                          photo.dateTime,
                          facilityUtcOffset,
                        )}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </ModalBody>
      </Modal>

      <ImageGalleryModal
        isOpen={imagesModal.isOpen}
        onClose={imagesModal.close}
        title="Image Viewer"
        images={
          Array.isArray(imagesModal?.record?.urls)
            ? imagesModal.record.urls
            : []
        }
        initialIndex={imagesModal?.record?.initialIndex || 0}
        zipName="stay-photos.zip"
      />
    </>
  );
}
